import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ArrowUp, ArrowDown, RotateCcw, Edit, Eye, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import InventoryService from '@/api/inventory.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

interface StockMovement {
  id: number;
  publicId: string;
  businessId: number;
  movementNumber: string;
  itemId: number;
  itemName: string;
  itemSku: string;
  movementType: 'in' | 'out' | 'adjustment' | 'transfer';
  quantity: number;
  unitCost?: number;
  totalValue: number;
  reason: string;
  reference?: string;
  fromLocation?: string;
  toLocation?: string;
  performedBy?: string;
  notes?: string;
  dateCreated: string;
  dateUpdated: string;
}

const StockMovements: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchMovements();
  }, [businessId]);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const response = await InventoryService.getStockMovements(businessId);
      
      if (response.data.result === 'success') {
        setMovements(response.data?.movements || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch stock movements",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching stock movements:', error);
      toast({
        title: "Error",
        description: "Failed to fetch stock movements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getMovementIcon = (type: string) => {
    switch (type) {
      case 'in':
        return <ArrowDown className="h-4 w-4 text-green-600" />;
      case 'out':
        return <ArrowUp className="h-4 w-4 text-red-600" />;
      case 'adjustment':
        return <RotateCcw className="h-4 w-4 text-blue-600" />;
      case 'transfer':
        return <Package className="h-4 w-4 text-purple-600" />;
      default:
        return <Package className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getMovementBadge = (type: string) => {
    const typeConfig = {
      in: { variant: 'default' as const, label: 'Stock In' },
      out: { variant: 'destructive' as const, label: 'Stock Out' },
      adjustment: { variant: 'secondary' as const, label: 'Adjustment' },
      transfer: { variant: 'outline' as const, label: 'Transfer' },
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.adjustment;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const filteredMovements = movements.filter(movement => {
    const matchesSearch = movement.movementNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         movement.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         movement.itemSku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         movement.reason.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || movement.movementType === typeFilter;
    return matchesSearch && matchesType;
  });

  const totalValue = filteredMovements.reduce((sum, movement) => sum + movement.totalValue, 0);
  const stockInCount = filteredMovements.filter(m => m.movementType === 'in').length;
  const stockOutCount = filteredMovements.filter(m => m.movementType === 'out').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stock Movements</h1>
          <p className="text-muted-foreground">
            Track all inventory stock movements and adjustments
          </p>
        </div>
        {canCreate('Inventory') && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Record Movement
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Movement Types</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/inventory/movements/create?type=in')}>
                <ArrowDown className="mr-2 h-4 w-4 text-green-600" />
                Stock In
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/inventory/movements/create?type=out')}>
                <ArrowUp className="mr-2 h-4 w-4 text-red-600" />
                Stock Out
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/inventory/movements/create?type=adjustment')}>
                <RotateCcw className="mr-2 h-4 w-4 text-blue-600" />
                Adjustment
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/inventory/movements/create?type=transfer')}>
                <Package className="mr-2 h-4 w-4 text-purple-600" />
                Transfer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Movements</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredMovements.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stock In</CardTitle>
            <ArrowDown className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stockInCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stock Out</CardTitle>
            <ArrowUp className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stockOutCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search movements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Types</option>
              <option value="in">Stock In</option>
              <option value="out">Stock Out</option>
              <option value="adjustment">Adjustment</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Movements Table */}
      <Card>
        <CardHeader>
          <CardTitle>Stock Movements ({filteredMovements.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Movement #</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMovements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <Package className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">No stock movements found</p>
                          {canCreate('Inventory') && (
                            <Button onClick={() => navigate('/inventory/movements/create')} size="sm">
                              Record your first movement
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMovements.map((movement) => (
                      <TableRow key={movement.id}>
                        <TableCell>
                          <div className="font-medium">{movement.movementNumber}</div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{movement.itemName}</div>
                            <div className="text-sm text-muted-foreground">SKU: {movement.itemSku}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getMovementIcon(movement.movementType)}
                            {getMovementBadge(movement.movementType)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={`font-medium ${
                            movement.movementType === 'in' ? 'text-green-600' : 
                            movement.movementType === 'out' ? 'text-red-600' : 
                            'text-blue-600'
                          }`}>
                            {movement.movementType === 'out' ? '-' : '+'}{movement.quantity}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{formatCurrency(movement.totalValue)}</div>
                          {movement.unitCost && (
                            <div className="text-sm text-muted-foreground">
                              @ {formatCurrency(movement.unitCost)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{movement.reason}</div>
                          {movement.reference && (
                            <div className="text-xs text-muted-foreground">Ref: {movement.reference}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {movement.movementType === 'transfer' ? (
                              <>
                                <div>From: {movement.fromLocation || 'N/A'}</div>
                                <div>To: {movement.toLocation || 'N/A'}</div>
                              </>
                            ) : (
                              <div>{movement.toLocation || movement.fromLocation || 'Default'}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{new Date(movement.dateCreated).toLocaleDateString()}</div>
                            <div className="text-muted-foreground">
                              {new Date(movement.dateCreated).toLocaleTimeString()}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <div className="h-4 w-4">⋮</div>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              {canView('Inventory') && (
                                <DropdownMenuItem onClick={() => navigate(`/inventory/movements/${movement.publicId}`)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                              )}
                              {canEdit('Inventory') && (
                                <DropdownMenuItem onClick={() => navigate(`/inventory/movements/${movement.publicId}/edit`)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Movement
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => navigate(`/inventory/items/${movement.itemId}`)}>
                                <Package className="mr-2 h-4 w-4" />
                                View Item
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StockMovements; 